import { useEffect, useCallback, useRef } from 'react';
import {
  useRefreshTokenMutation,
  useLogoutMutation
} from '@/services/auth.service';
import { isTokenExpiringSoon, getTimeUntilExpiry } from '@/utils/jwtUtils';
import useAuthStore from '@/stores/authStore';
import { refreshAccessToken } from '@/services/base.service';
import { REFRESH_TOKEN_TIME } from '@/constants';

// Default refresh token time in seconds (14 minutes)
const DEFAULT_REFRESH_TOKEN_TIME = 14 * 60;

// Global flag to prevent multiple instances of the hook from setting up intervals
let globalRefreshInterval: NodeJS.Timeout | null = null;
let globalRefreshSetup = false;

export const useAuth = () => {
  const {
    userInfo,
    hasValidToken,
    hasValidRefreshToken,
    getToken,
    getRefreshToken,
    resetState
  } = useAuthStore();

  const refreshTokenMutation = useRefreshTokenMutation();
  const logoutMutation = useLogoutMutation();

  // Flag to prevent multiple simultaneous refresh calls
  const isRefreshingRef = useRef(false);

  // Check if user is authenticated
  const isAuthenticated = useCallback(() => {
    const hasToken = hasValidToken();
    const hasUser = userInfo.user && userInfo.user.id;
    return hasToken && hasUser;
  }, [hasValidToken, userInfo.user, userInfo.token]);

  // Check if refresh token is valid
  const canRefresh = useCallback(() => {
    return hasValidRefreshToken();
  }, [hasValidRefreshToken]);

  // Log token expiration times for debugging
  const logTokenStatus = useCallback(() => {
    const token = getToken();
    const refreshToken = getRefreshToken();

    if (token) {
      getTimeUntilExpiry(token);
    }

    if (refreshToken) {
      getTimeUntilExpiry(refreshToken);
    }
  }, [getToken, getRefreshToken]);

  // Automatic token refresh when token is expiring soon
  const refreshTokenIfNeeded = useCallback(async () => {
    // Prevent multiple simultaneous calls
    if (isRefreshingRef.current) {
      return true;
    }

    const token = getToken();
    const refreshToken = getRefreshToken();

    if (!token || !refreshToken) {
      return false;
    }

    // Log token status for debugging
    logTokenStatus();

    // Check if refresh token is still valid
    if (!hasValidRefreshToken()) {
      await logoutMutation.mutateAsync();
      return false;
    }

    // Get refresh token time from environment or use default
    const refreshTime =
      Number(REFRESH_TOKEN_TIME) || DEFAULT_REFRESH_TOKEN_TIME;

    // Check if token is expiring soon
    if (isTokenExpiringSoon(token, refreshTime)) {
      isRefreshingRef.current = true;

      try {
        const newToken = await refreshAccessToken();
        if (newToken) {
          return true;
        } else {
          throw new Error('Token refresh returned null');
        }
      } catch {
        // If refresh fails, logout user immediately
        await logoutMutation.mutateAsync();
        return false;
      } finally {
        isRefreshingRef.current = false;
      }
    }

    return true;
  }, [
    getToken,
    getRefreshToken,
    hasValidRefreshToken,
    logoutMutation,
    logTokenStatus
  ]);

  // Manual logout
  const logout = useCallback(async () => {
    try {
      await logoutMutation.mutateAsync();
    } catch {
      // Even if logout API fails, clear local state
      resetState();
    }
  }, [logoutMutation, resetState]);

  // Set up automatic token refresh only when authenticated
  useEffect(() => {
    const token = getToken();
    const user = userInfo.user;

    if (!token || !user?.id) {
      return;
    }

    if (!isAuthenticated()) {
      return;
    }

    // Prevent multiple instances from setting up intervals
    if (globalRefreshSetup) {
      return;
    }

    // Clear any existing interval
    if (globalRefreshInterval) {
      clearInterval(globalRefreshInterval);
    }

    // Check if token needs refresh on mount (only once)
    const initialCheck = async () => {
      try {
        await refreshTokenIfNeeded();
      } catch {
        // Handle error silently
      }
    };
    initialCheck();

    // Set up interval to check token expiration
    globalRefreshInterval = setInterval(() => {
      refreshTokenIfNeeded();
    }, 15000); // Check every 15 seconds for more responsive refresh

    globalRefreshSetup = true;

    return () => {
      // Only clear if this is the last instance
      if (globalRefreshInterval) {
        clearInterval(globalRefreshInterval);
        globalRefreshInterval = null;
        globalRefreshSetup = false;
      }
    };
  }, [isAuthenticated, getToken, userInfo.user, refreshTokenIfNeeded]);

  return {
    // State
    user: userInfo.user,
    token: userInfo.token,
    refreshToken: userInfo.refresh_token,
    isAuthenticated: isAuthenticated(),
    canRefresh: canRefresh(),

    // Actions
    logout,
    refreshTokenAsync: refreshTokenMutation.mutateAsync,

    // Loading states
    isRefreshing: refreshTokenMutation.isPending,
    isLoggingOut: logoutMutation.isPending,

    // Utilities
    refreshTokenIfNeeded,
    hasValidToken,
    hasValidRefreshToken
  };
};
